import { QueryKey } from '@/configs/queryKeys';
import { Client, Merchant } from '@/types/user';
import { BaseEntity, UUID } from '@/types/types';
import { Product } from './ProductApi';
import { CreatorOptions } from '@/types/creator';
import { fileTypeToHeader } from '@/utils/utilities';
import { ExportType } from './OfferApi';
import httpClient from './httpClient';
import EntityApi from './EntityApi';

export enum OrderStatus {
	PENDING = 'pending',
	PROCESSING = 'processing',
	COMPLETED = 'completed',
	CANCELLED = 'cancelled',
}

export interface OrderItem extends BaseEntity {
	orderId: UUID;
	product: Product;
	quantity: number;
	deliveryDate: string;
	comment: string;
}

export interface CustomOrderItem {
	name: string;
	taxRate: number;
	netPrice: number;
}

export interface ShippingDetails {
	shippingName: string;
	shippingStreet: string;
	shippingCity: string;
	shippingPostalCode: string;
	shippingPhoneNumber: string;
	shippingComment: string;
}

export interface Order extends BaseEntity, ShippingDetails {
	client: Client;
	merchant?: Merchant;
	number: number;
	totalPrice: number;
	subtotal: number;
	orderItems: OrderItem[];
	customOrderItems: CustomOrderItem[];
	status: OrderStatus;
	offerId?: UUID;
}

class OrderApi extends EntityApi<Order> {
	constructor() {
		super(QueryKey.orders);
		this.submitOrder = this.submitOrder.bind(this);
		this.copyOrder = this.copyOrder.bind(this);
		this.addProductToOrder = this.addProductToOrder.bind(this);
		this.deleteOrderItem = this.deleteOrderItem.bind(this);
		this.updateOrderItem = this.updateOrderItem.bind(this);
		this.createFromOffer = this.createFromOffer.bind(this);
		this.generatePdf = this.generatePdf.bind(this);
	}

	async addProductToOrder({
		orderId,
		product,
		options,
		quantity,
	}: {
		orderId: UUID;
		product: Product;
		options: CreatorOptions;
		quantity: number;
	}) {
		const response = await httpClient.post<Order>(
			`${this.queryKey}/${orderId}/add-to-order`,
			{
				product: {
					id: product.id,
					productType: { id: product.productType.id },
				},
				quantity,
			}
		);

		return response.data;
	}

	async getOrderItem(orderId: UUID) {
		const response = await httpClient.get<OrderItem>(
			`${QueryKey.orderItems}/${orderId}`
		);
		return response.data;
	}

	async deleteOrderItem({
		orderId,
		orderItemId,
	}: {
		orderId: UUID;
		orderItemId: UUID;
	}) {
		await httpClient.delete(`${this.queryKey}/${orderId}/items/${orderItemId}`);
		return 1;
	}

	async updateOrderItem({
		orderId,
		payload,
	}: {
		orderId: UUID;
		payload: Pick<OrderItem, 'id' | 'quantity'>;
	}) {
		const response = await httpClient.patch<Order>(
			`${this.queryKey}/${orderId}/items/${payload.id}`,
			payload
		);
		return response.data;
	}

	async submitOrder({
		orderId,
		payload,
	}: {
		orderId: UUID;
		payload: Partial<Order>;
	}) {
		await super.update({
			id: orderId,
			...payload,
		});

		const response = await httpClient.patch<Order>(
			`${this.queryKey}/${orderId}/${QueryKey.changeStatus}`,
			{
				status: OrderStatus.PROCESSING,
			}
		);
		return response.data;
	}

	async copyOrder(orderId: UUID) {
		const response = await httpClient.post<Order>(
			`${this.queryKey}/${orderId}/${QueryKey.duplicate}`,
			{
				orderId,
			}
		);
		return response.data;
	}

	async createFromOffer(offerId: UUID) {
		const response = await httpClient.post<Order>(
			`${this.queryKey}/from-offer`,
			{
				offerId,
			}
		);
		return response.data;
	}

	async generatePdf({
		order,
		exportType,
	}: {
		order: Order;
		exportType: ExportType;
	}) {
		const response = await httpClient.get<Blob>(
			`${this.queryKey}/${order.id}/export/${exportType}`,
			{
				headers: {
					Accept: fileTypeToHeader('pdf'),
				},
				responseType: 'blob',
			}
		);

		return response.data;
	}
}

export default new OrderApi();
